import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { X, ArrowLeft, Copy as CopyIcon, Check, Upload, Landmark, Ticket } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useStoreSettings } from '../lib/useStoreSettings';
import { useCurrency } from '../context/CurrencyContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback payment methods used only if the /api/payment-methods fetch fails.
// The real values are loaded dynamically at mount so the admin can edit them
// via the Payments panel without a redeploy.
const CoinIcon = ({ color, char }) => (
  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] font-bold" style={{ background: color || '#1e293b' }}>{char || '?'}</span>
);

const COIN_COLORS = {
  BTC: '#F7931A',
  ETH: '#627EEA',
  SOL: '#14F195',
  USDT: '#26A17B',
  USDC: '#2775CA',
  PYUSD: '#0070E0',
};

const StepNumber = ({ n }) => (
  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand text-white text-[13px] font-semibold shadow-sm">{n}</span>
);

const Field = ({ label, required = false, error = '', ...props }) => (
  <div>
    <label className="block text-[10px] tracking-[0.12em] text-neutral-500 dark:text-neutral-400 uppercase mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      className={`w-full bg-white dark:bg-[#0f0f10] border rounded-md px-2.5 py-2 text-[13.5px] text-neutral-900 dark:text-neutral-100 outline-none placeholder-neutral-400 transition-colors ${error ? 'border-red-400 focus:border-red-500' : 'border-neutral-200 dark:border-neutral-800 focus:border-neutral-400 dark:focus:border-neutral-600'}`}
      {...props}
    />
    {error && <p className="text-red-600 text-[11px] mt-0.5">{error}</p>}
  </div>
);

const CheckoutModal = () => {
  const {
    checkoutOpen, setCheckoutOpen, setCartOpen, cart,
    subtotal, shipping, total, clearCart, syncPrices,
  } = useApp();
  const { auth, headers: authHeaders, isAuthed, openAuth } = useCustomerAuth();

  // Defensive guard: block checkout for unauthenticated users
  useEffect(() => {
    if (checkoutOpen && !isAuthed) {
      setCheckoutOpen(false);
      openAuth(() => setTimeout(() => setCheckoutOpen(true), 150), 'Please sign in or create an account to place your order.');
    }
  }, [checkoutOpen, isAuthed, openAuth, setCheckoutOpen]);

  const [form, setForm] = useState({
    fullName: '', address: '', apt: '', city: '', state: '',
    postalCode: '', country: 'United States', email: '', telegram: '',
  });

  // Pre-fill email when user logs in / modal opens
  useEffect(() => {
    if (auth?.email && !form.email) {
      setForm(f => ({ ...f, email: auth.email }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.email, checkoutOpen]);
  const [network, setNetwork] = useState('bitcoin');
  const [copied, setCopied] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [txnId, setTxnId] = useState('');
  const [proofNote, setProofNote] = useState('');
  const [sent, setSent] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [orderToken, setOrderToken] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingOptionId, setShippingOptionId] = useState('');
  const [countries, setCountries] = useState([]);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponErr, setCouponErr] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [orderErr, setOrderErr] = useState('');
  const store = useStoreSettings();
  const { code: curCode, rate: curRate, isUSD, conv, fmt, fmtLocal, totals, reload: reloadFx } = useCurrency();
  useEffect(() => { if (checkoutOpen) reloadFx(); }, [checkoutOpen, reloadFx]);

  const cartItemsPayload = useMemo(() => cart.map(i => ({
    id: String(i.id), name: i.name, strength: i.strength || '', packaging: i.packaging || '',
    catLabel: i.catLabel || '', price: Number(i.price), qty: Number(i.qty), image: i.image || '',
  })), [cart]);

  const applyCoupon = async (code, keepOrderErr = false) => {
    const c = (code || '').trim().toUpperCase();
    if (!c) return;
    setCouponBusy(true); setCouponErr(''); if (!keepOrderErr) setOrderErr('');
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, { code: c, items: cartItemsPayload }, { headers: authHeaders });
      setCoupon(data); setCouponInput(data.code);
    } catch (e) {
      setCoupon(null);
      setCouponErr(e?.response?.data?.detail || 'Could not apply coupon');
    } finally { setCouponBusy(false); }
  };

  // Refresh cart prices from the server when checkout opens (avoids stale localStorage prices)
  useEffect(() => {
    if (!checkoutOpen || !cart.length) return;
    axios.post(`${API}/cart/quote`, cartItemsPayload).then(({ data }) => syncPrices(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutOpen]);

  const removeCoupon = () => { setCoupon(null); setCouponInput(''); setCouponErr(''); setOrderErr(''); };

  // Re-validate an applied coupon whenever the cart changes (min order / % amount)
  useEffect(() => {
    if (coupon && cart.length) applyCoupon(coupon.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItemsPayload]);

  // Load countries the store ships to (admin-managed)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/countries`);
        if (cancelled || !Array.isArray(data)) return;
        setCountries(data);
        // If the current form.country isn't in the list, snap to the first one.
        if (data.length > 0 && !data.some(c => c.name === form.country)) {
          setForm(f => ({ ...f, country: data[0].name }));
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutOpen]);

  // Load shipping options — re-fetches whenever the country changes so
  // per-country regional overrides are reflected instantly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const q = form.country ? `?country=${encodeURIComponent(form.country)}` : '';
        const { data } = await axios.get(`${API}/shipping-options${q}`);
        if (cancelled || !Array.isArray(data)) return;
        setShippingOptions(data);
        // If the current selection was hidden for this country, snap to first.
        setShippingOptionId(prev => {
          if (data.length === 0) return '';
          if (prev && data.some(d => d.id === prev)) return prev;
          return data[0].id;
        });
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [checkoutOpen, form.country]);

  const [autoShip, setAutoShip] = useState(null);
  useEffect(() => {
    if (!checkoutOpen) return undefined;
    let cancelled = false;
    const h = setTimeout(() => axios.get(`${API}/shipping-auto`, { params: { country: form.country || '', subtotal } })
      .then(({ data }) => { if (!cancelled) setAutoShip(data?.enabled && data.option ? data.option : null); })
      .catch(() => {}), 200);
    return () => { cancelled = true; clearTimeout(h); };
  }, [checkoutOpen, form.country, subtotal]);

  const chosenShipping = useMemo(
    () => autoShip || shippingOptions.find(s => s.id === shippingOptionId),
    [autoShip, shippingOptions, shippingOptionId],
  );
  const effectiveShipping = chosenShipping ? Number(chosenShipping.price) : shipping;
  const discount = coupon ? Math.min(Number(coupon.discount) || 0, Number(subtotal)) : 0;
  const effectiveTotal = +(Number(subtotal) - discount + effectiveShipping).toFixed(2);
  const loc = totals(cart, effectiveShipping, discount);
  const usdRef = isUSD ? '' : ` (≈ $${effectiveTotal.toFixed(2)} USD)`;

  // Load payment methods from the API (admin can edit these in real time).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payment-methods`);
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setPaymentMethods(data);
          // Ensure the currently-selected network exists in the fresh list.
          if (!data.some(m => m.id === network)) setNetwork(data[0].id);
        }
      } catch { /* silent — the modal simply won't render options */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutOpen]);

  const paymentMap = useMemo(() => Object.fromEntries(paymentMethods.map(m => [m.id, m])), [paymentMethods]);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const p = paymentMap[network];

  // Required-field errors (only shown after first submit attempt)
  const errors = useMemo(() => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.address.trim()) e.address = 'Address is required';
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.state.trim()) e.state = 'State is required';
    if (!form.postalCode.trim()) e.postalCode = 'Postal code is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    return e;
  }, [form]);

  const canSend = Object.keys(errors).length === 0;
  const showErr = (key) => attempted ? errors[key] || '' : '';

  const copyAddress = async () => {
    try {
      const textToCopy = p?.kind === 'bank'
        ? Object.entries(p?.beneficiary).map(([k, v]) => `${k}: ${v}`).join('\n')
        : p?.address;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  const submit = async () => {
    setAttempted(true);
    if (!canSend) {
      // Scroll to the first field with an error
      const first = ['fullName','address','city','state','postalCode','email'].find(k => errors[k]);
      if (first) {
        const el = document.querySelector(`[data-field="${first}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    // Build payload
    const payload = {
      form,
      network: p?.label,
      items: cartItemsPayload,
      subtotal: Number(subtotal),
      shipping: Number(effectiveShipping),
      currency: curCode,
      fx_rate: curRate,
      shipping_option_id: chosenShipping?.id || shippingOptionId || undefined,
      shipping_label: chosenShipping?.label || undefined,
      discountAmount: discount,
      coupon_code: coupon?.code || undefined,
      total: Number(effectiveTotal),
    };
    setOrderErr('');
    try {
      const { data } = await axios.post(`${API}/orders`, payload, { headers: authHeaders });
      setOrderId(data.id);
      setOrderToken(data.access_token || '');
      if ((screenshot || txnId.trim()) && data.access_token) {
        const fd = new FormData();
        fd.append('t', data.access_token); fd.append('transaction_id', txnId.trim());
        if (screenshot) fd.append('file', screenshot);
        try { await axios.post(`${API}/order-portal/${data.id}/payment-proof`, fd); setProofNote('ok'); }
        catch (ex) { setProofNote(ex?.response?.data?.detail || 'Your payment proof could not be uploaded'); }
      }
    } catch (e) {
      if (e?.response?.data?.detail) {
        setOrderErr(typeof e.response.data.detail === 'string' ? e.response.data.detail : 'Order failed. Please review your details.');
        if (coupon) applyCoupon(coupon.code, true);
        if (e.response.status === 409) reloadFx();
        return;
      }
      // Fallback: local order id if backend unreachable
      setOrderId('BG-' + Date.now().toString(36).toUpperCase());
    }
    setSent(true);
    try {
      const orders = JSON.parse(localStorage.getItem('ep_orders') || '[]');
      orders.push({ ...payload, at: new Date().toISOString() });
      localStorage.setItem('ep_orders', JSON.stringify(orders));
    } catch {}
    clearCart();
  };

  const close = () => {
    setCheckoutOpen(false);
    setSent(false);
    setOrderId('');
    setScreenshot(null); setTxnId(''); setProofNote('');
    removeCoupon();
  };

  const backToCart = () => {
    setCheckoutOpen(false);
    setTimeout(() => setCartOpen(true), 60);
  };

  if (!checkoutOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-start justify-center p-3 md:p-6 overflow-y-auto">
      <div className="relative bg-white dark:bg-[#141416] rounded-2xl w-full max-w-5xl my-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 md:px-8 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <button onClick={backToCart} className="inline-flex items-center gap-1.5 text-[13.5px] text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md px-2.5 py-1.5">
            <ArrowLeft size={14} /> Back to cart
          </button>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100">Checkout</h2>
          <button onClick={close} className="w-9 h-9 rounded-full border border-neutral-200 dark:border-neutral-700 flex items-center justify-center hover:bg-neutral-50 dark:hover:bg-neutral-800">
            <X size={16} />
          </button>
        </div>

        {sent ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mb-4">
              <Check size={26} />
            </div>
            <h3 className="font-serif text-[30px] text-neutral-900 dark:text-neutral-100 mb-2">Order received</h3>
            <p className="text-neutral-500 dark:text-neutral-400 text-[14px] mb-4">We've got it. Once your {p?.kind === 'bank' ? 'bank transfer' : `${p?.label} payment`} is confirmed we'll ship within 24 hours.</p>
            <div className="inline-flex items-center gap-2 bg-neutral-100 dark:bg-neutral-800 rounded-md px-3 py-2 text-[13px]">
              <span className="text-neutral-500">Order ID</span>
              <span className="font-mono text-neutral-900 dark:text-neutral-100">{orderId}</span>
            </div>
            {proofNote === 'ok' && <p data-testid="checkout-proof-ok" className="mt-5 text-[13px] text-emerald-600">Payment proof received — thank you.</p>}
            {proofNote && proofNote !== 'ok' && <p data-testid="checkout-proof-error" className="mt-5 text-[13px] text-red-600">{proofNote}. Please add it from your order page.</p>}
            {orderToken && !proofNote && (
              <p className="mt-5 text-[13px] text-neutral-600 dark:text-neutral-300">Already paid? Upload your payment screenshot and transaction ID on your order page.</p>
            )}
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {orderToken && <Link data-testid="checkout-manage-order" to={`/order/${orderId}?t=${orderToken}`} onClick={close} className="rounded-lg border border-brand text-brand px-6 py-2.5 text-[14px] font-semibold">{proofNote === 'ok' ? 'View your order' : 'Upload payment proof'}</Link>}
              <button onClick={close} className="btn-brand rounded-lg px-6 py-2.5 text-[14px] font-semibold">Continue shopping</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6 md:gap-8 px-6 md:px-8 py-6">
            {/* Left column */}
            <div className="space-y-6">
              {/* Section 1 */}
              <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-4">
                  <StepNumber n={1} />
                  <h3 className="text-[15px] font-medium text-neutral-900 dark:text-neutral-100">Shipping information</h3>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div data-field="fullName"><Field label="Full name" required error={showErr('fullName')} value={form.fullName} onChange={(e) => upd('fullName', e.target.value)} placeholder="John Doe" /></div>
                  <div data-field="address"><Field label="Address" required error={showErr('address')} value={form.address} onChange={(e) => upd('address', e.target.value)} placeholder="123 Main St" /></div>
                  <Field label="Apt, suite, unit" value={form.apt} onChange={(e) => upd('apt', e.target.value)} placeholder="Optional" />
                  <div className="grid grid-cols-2 gap-3">
                    <div data-field="city"><Field label="City" required error={showErr('city')} value={form.city} onChange={(e) => upd('city', e.target.value)} /></div>
                    <div data-field="state"><Field label="State" required error={showErr('state')} value={form.state} onChange={(e) => upd('state', e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div data-field="postalCode"><Field label="Postal code" required error={showErr('postalCode')} value={form.postalCode} onChange={(e) => upd('postalCode', e.target.value)} /></div>
                    <div>
                      <label className="block text-[10px] tracking-[0.12em] text-neutral-500 uppercase mb-1">Country</label>
                      <select data-testid="checkout-country-select" value={form.country} onChange={(e) => upd('country', e.target.value)} className="w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-2.5 py-2 text-[13.5px] text-neutral-900 dark:text-neutral-100 outline-none focus:border-neutral-400">
                        {countries.length === 0 ? (
                          <option value={form.country}>{form.country || 'Select country'}</option>
                        ) : (
                          countries.map((c) => (
                            <option key={c.id} value={c.name}>{c.flag ? c.flag + ' ' : ''}{c.name}</option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                  <div data-field="email"><Field label="Email" required error={showErr('email')} type="email" value={form.email} onChange={(e) => upd('email', e.target.value)} placeholder="your@email.com" /></div>
                  <Field label="Telegram (optional)" value={form.telegram} onChange={(e) => upd('telegram', e.target.value)} placeholder="@username" />
                </div>
              </div>

              {/* Section 1.5 - Shipping choice */}
              {autoShip && (
                <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-5" data-testid="shipping-auto-section">
                  <div className="flex items-center gap-3 mb-3">
                    <StepNumber n={2} />
                    <h3 className="text-[16px] font-medium text-neutral-900 dark:text-neutral-100">Shipping</h3>
                  </div>
                  <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/20 p-3.5 flex items-start justify-between gap-3">
                    <div>
                      <div data-testid="shipping-auto-label" className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-100">{autoShip.label}</div>
                      {autoShip.eta && <div className="text-[11.5px] text-neutral-500 mt-0.5">{autoShip.eta}</div>}
                      <div className="text-[11.5px] text-emerald-700 dark:text-emerald-400 mt-1">Selected automatically for {form.country || 'your country'} — nothing to choose.</div>
                    </div>
                    <div data-testid="shipping-auto-price" className="text-[13.5px] font-semibold text-neutral-900 dark:text-neutral-100 whitespace-nowrap">{fmt(autoShip.price)}</div>
                  </div>
                </div>
              )}
              {!autoShip && shippingOptions.length > 0 && (
                <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-5" data-testid="shipping-section">
                  <div className="flex items-center gap-3 mb-3">
                    <StepNumber n={2} />
                    <h3 className="text-[16px] font-medium text-neutral-900 dark:text-neutral-100">Choose shipping</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {shippingOptions.map((s) => {
                      const active = shippingOptionId === s.id;
                      return (
                        <button
                          type="button"
                          key={s.id}
                          data-testid={`shipping-tile-${s.id}`}
                          onClick={() => setShippingOptionId(s.id)}
                          className={`text-left rounded-lg border p-3 transition-colors ${active ? 'border-[#1e293b] bg-white dark:bg-neutral-900 ring-1 ring-[#1e293b]' : 'border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:border-neutral-400'}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-100">{s.label}</div>
                            <div className="text-[13.5px] font-semibold text-neutral-900 dark:text-neutral-100">{fmt(s.price)}</div>
                          </div>
                          {s.eta && <div className="text-[11.5px] text-neutral-500 mt-0.5">{s.eta}</div>}
                          {s.description && <div className="text-[11px] text-neutral-500 mt-1 line-clamp-2">{s.description}</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Section 2 - Payment */}
              <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <StepNumber n={3} />
                  <h3 className="text-[16px] font-medium text-neutral-900 dark:text-neutral-100">Send payment</h3>
                </div>
                <p className="text-[13.5px] text-neutral-500 dark:text-neutral-400 mb-4">Send exactly <span data-testid="checkout-send-amount" className="font-semibold text-neutral-900 dark:text-neutral-100">{fmtLocal(loc.total)}</span>{usdRef && <span data-testid="checkout-usd-ref" className="text-neutral-500">{usdRef}</span>} {p?.kind === 'bank' ? 'via one of the providers below.' : 'on the network you choose below.'}</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  {paymentMethods.map((cfg) => {
                    const active = network === cfg.id;
                    return (
                      <button
                        key={cfg.id}
                        data-testid={`payment-tile-${cfg.id}`}
                        onClick={() => setNetwork(cfg.id)}
                        className={`flex items-center justify-center gap-2 rounded-lg py-3 px-2 text-[12.5px] font-medium border transition-colors text-center leading-tight ${active ? 'border-[#1e293b] bg-white dark:bg-neutral-900 shadow-sm ring-1 ring-[#1e293b]' : 'border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 hover:border-neutral-400'}`}
                      >
                        {cfg.kind === 'bank' ? (
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-white" style={{ background: cfg.color || '#0F766E' }}><Landmark size={9} /></span>
                        ) : (
                          <CoinIcon color={cfg.color} char={cfg.icon} />
                        )}
                        <span>{cfg.label}</span>
                      </button>
                    );
                  })}
                </div>

                {p && (p?.kind === 'crypto' ? (
                  <>
                    <div className="text-[13px] text-neutral-500 dark:text-neutral-400 mb-3">
                      Send <span className="font-medium text-neutral-800 dark:text-neutral-200">{p?.coins.length > 1 ? 'any of these coins' : p?.coins[0]}</span> on the <span className="font-semibold text-neutral-900 dark:text-neutral-100">{p?.network}</span> network:
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {p?.coins.map(c => (
                        <span key={c} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-800 rounded-full px-2.5 py-1 text-[12px] text-neutral-700 dark:text-neutral-200">
                          <span className="inline-block w-3.5 h-3.5 rounded-full" style={{ background: COIN_COLORS[c] || '#999' }} />
                          {c}
                        </span>
                      ))}
                    </div>

                    <div className="text-[12px] text-neutral-500 mb-1.5">↓ Wallet address on {p?.network}:</div>
                    <div className="flex items-stretch border border-neutral-200 dark:border-neutral-800 rounded-md overflow-hidden">
                      <div className="flex-1 px-3 py-2.5 text-[13px] font-mono text-neutral-800 dark:text-neutral-100 break-all bg-neutral-50 dark:bg-neutral-900">{p?.address}</div>
                      <button onClick={copyAddress} className="px-4 border-l border-neutral-200 dark:border-neutral-800 text-[13px] font-medium hover:bg-neutral-100 dark:hover:bg-neutral-800">
                        {copied ? <span className="inline-flex items-center gap-1 text-emerald-600"><Check size={14} /> Copied</span> : <span className="inline-flex items-center gap-1"><CopyIcon size={14} /> Copy</span>}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-[13px] text-neutral-500 dark:text-neutral-400 mb-3">
                      Pay via any of these providers — use the beneficiary details below:
                    </div>
                    <div className="flex flex-wrap gap-2 mb-5">
                      {p?.providers.map(pr => (
                        <span key={pr} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-800 rounded-full px-3 py-1.5 text-[12px] text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-900">
                          <Landmark size={12} className="text-[#0F766E]" />
                          {pr}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[12px] text-neutral-500">Beneficiary details</div>
                      <button onClick={copyAddress} className="text-[12px] font-medium text-neutral-700 dark:text-neutral-200 inline-flex items-center gap-1 hover:text-neutral-900">
                        {copied ? <><Check size={13} className="text-emerald-600" /> Copied</> : <><CopyIcon size={13} /> Copy all</>}
                      </button>
                    </div>
                    <div className="border border-neutral-200 dark:border-neutral-800 rounded-md overflow-hidden bg-neutral-50 dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-800">
                      {Object.entries(p?.beneficiary).map(([k, v]) => (
                        <div key={k} className="grid grid-cols-[130px_1fr] items-center px-3 py-2 text-[13px]">
                          <div className="text-[11px] tracking-wider uppercase text-neutral-500">{k}</div>
                          <div className="font-mono text-neutral-800 dark:text-neutral-100 break-all">{v}</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11.5px] text-neutral-500 mt-3">
                      For international transfers use the recipient details exactly. Reference: your order email or telegram handle so we can match your payment.
                    </p>
                  </>
                ))}
              </div>

              {/* Section 3 - Attach payment screenshot */}
              <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <StepNumber n={4} />
                  <h3 className="text-[16px] font-medium text-neutral-900 dark:text-neutral-100">Attach payment screenshot</h3>
                </div>
                <p className="text-[13.5px] text-neutral-500 dark:text-neutral-400 mb-4">Paid already? Attach a screenshot and/or the transaction ID. You can also add it later from your order page.</p>
                <input data-testid="checkout-txn-input" value={txnId} onChange={(e) => setTxnId(e.target.value)} maxLength={200} placeholder="Transaction ID / hash / reference (optional)"
                  className="w-full mb-3 bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2.5 text-[13.5px] font-mono outline-none focus:border-neutral-400" />
                <label className="block border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg py-8 text-center cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors">
                  <input data-testid="checkout-screenshot-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setScreenshot(e.target.files?.[0] || null)} />
                  <Upload size={20} className="mx-auto mb-2 text-neutral-400" />
                  <span className="text-[13.5px] text-neutral-600 dark:text-neutral-300">{screenshot?.name || 'Tap to attach screenshot'}</span>
                </label>
              </div>

              {/* Section 4 - Send order */}
              <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <StepNumber n={5} />
                  <h3 className="text-[16px] font-medium text-neutral-900 dark:text-neutral-100">Send your order</h3>
                </div>
                <p className="text-[13.5px] text-neutral-500 dark:text-neutral-400 mb-4">
                  Fill in your shipping info above (fields marked <span className="text-red-500">*</span> are required), then click <span className="font-semibold text-neutral-900 dark:text-neutral-100">Send Order</span>.
                </p>
                {attempted && !canSend && (
                  <div className="mb-3 rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-[13px] text-red-700 dark:text-red-300">
                    Please complete all required fields: {Object.keys(errors).map(k => ({
                      fullName: 'Full Name', address: 'Address', city: 'City', state: 'State', postalCode: 'Postal Code', email: 'Email'
                    }[k])).join(', ')}.
                  </div>
                )}
                {orderErr && (
                  <div data-testid="checkout-order-error" className="mb-3 rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-[13px] text-red-700 dark:text-red-300">{orderErr}</div>
                )}
                <button
                  data-testid="checkout-send-order"
                  onClick={submit}
                  disabled={sent}
                  className={`w-full rounded-lg py-3.5 text-[15px] font-semibold transition-colors ${canSend ? 'btn-brand cursor-pointer' : 'bg-neutral-300 dark:bg-neutral-700 text-white hover:bg-neutral-400 dark:hover:bg-neutral-600 cursor-pointer'}`}
                >
                  {canSend ? `Send Order — ${fmtLocal(loc.total)}` : 'Send Order'}
                </button>
                <p className="text-[11.5px] text-neutral-500 dark:text-neutral-400 mt-3 text-center">
                  Having trouble? Copy your order below and paste it into an email{store?.support_email ? <> to <a data-testid="checkout-support-email" href={`mailto:${store.support_email}`} className="text-neutral-800 dark:text-neutral-200 font-medium underline underline-offset-2">{store.support_email}</a></> : ' to our support team'}.
                </p>
              </div>
            </div>

            {/* Right column - order summary */}
            <aside>
              <div className="md:sticky md:top-4 border border-neutral-200 dark:border-neutral-800 rounded-xl p-5 bg-white dark:bg-[#141416]">
                <h3 className="text-[16px] font-medium text-neutral-900 dark:text-neutral-100 mb-4">Order summary</h3>
                <div className="space-y-3 mb-4 max-h-[220px] overflow-y-auto pr-1">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-100 truncate">{item.name}</div>
                        <div className="text-[12px] text-neutral-500 dark:text-neutral-400">{item.strength} - {item.packaging}</div>
                        <div className="text-[11.5px] text-neutral-500 dark:text-neutral-400 mt-0.5">Qty {item.qty}</div>
                      </div>
                      <div className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-100 whitespace-nowrap">{fmtLocal(conv(item.price) * item.qty)}</div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-neutral-200 dark:border-neutral-800 pt-3 pb-3" data-testid="coupon-section">
                  {coupon ? (
                    <div className="flex items-center justify-between rounded-lg border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-[13px] text-emerald-700 dark:text-emerald-300 font-medium"><Ticket size={14} /> <span data-testid="applied-coupon-code" className="font-mono">{coupon.code}</span> applied</span>
                      <button data-testid="coupon-remove-btn" onClick={removeCoupon} className="text-[12px] text-neutral-500 hover:text-red-500 underline">Remove</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        data-testid="coupon-input"
                        value={couponInput}
                        onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponErr(''); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') applyCoupon(couponInput); }}
                        placeholder="Coupon code"
                        className="flex-1 min-w-0 bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-2.5 py-2 text-[13px] font-mono uppercase text-neutral-900 dark:text-neutral-100 outline-none focus:border-neutral-400"
                      />
                      <button data-testid="coupon-apply-btn" onClick={() => applyCoupon(couponInput)} disabled={couponBusy || !couponInput.trim()} className="btn-brand rounded-md px-3.5 py-2 text-[13px] font-semibold disabled:opacity-50">{couponBusy ? '…' : 'Apply'}</button>
                    </div>
                  )}
                  {coupon?.category_labels?.length > 0 && (
                    <p data-testid="coupon-scope-note" className="text-[11.5px] text-neutral-500 mt-1.5">Applies to {coupon.category_labels.join(', ')} items ({fmt(coupon.eligible_subtotal)} eligible)</p>
                  )}
                  {couponErr && <p data-testid="coupon-error" className="text-red-600 text-[11.5px] mt-1.5">{couponErr}</p>}
                </div>

                <div className="border-t border-neutral-200 dark:border-neutral-800 pt-3 space-y-2 text-[13.5px]">
                  <div className="flex justify-between"><span className="text-neutral-600 dark:text-neutral-300">Subtotal</span><span data-testid="checkout-subtotal">{fmtLocal(loc.subtotal)}</span></div>
                  {discount > 0 && (
                    <div className="flex justify-between text-emerald-600" data-testid="checkout-discount-row"><span>Discount ({coupon.code})</span><span data-testid="checkout-discount-amount">-{fmtLocal(loc.discount)}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-neutral-600 dark:text-neutral-300">Shipping{chosenShipping ? ` (${chosenShipping.label})` : ''}</span><span data-testid="checkout-shipping-total">{fmtLocal(loc.shipping)}</span></div>
                </div>

                <div className="flex items-baseline justify-between mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-800">
                  <span className="text-[14.5px] text-neutral-900 dark:text-neutral-100">Total</span>
                  <span className="text-right"><span data-testid="checkout-order-total" className="block text-[22px] font-semibold text-neutral-900 dark:text-neutral-100">{fmtLocal(loc.total)}</span>{!isUSD && <span data-testid="checkout-total-usd" className="block text-[11px] text-neutral-500">≈ ${effectiveTotal.toFixed(2)} USD</span>}</span>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutModal;
